import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { inNativeApp } from "./native";
import { keepPageInPlace } from "../ui/scroll";
import { ApiError, createApiClient } from "../api/client";
import { choosePendingStore } from "../api/pending";
import type { AppSession, DeviceStatus, StaffMember } from "../api/types";
import { t } from "../i18n";
import { keptPairing } from "../app/kept-pairing";
import { clearSize } from "../app/size";
import { Opening } from "../app/Opening";
import { ToastProvider } from "../ui/toasts";
import { AppPairing, AppSignedOut, AppSignIn, AppWaiting, useWideScreen, WideScreen } from "./AppScreens";
import { AppShell } from "./AppShell";
import { APP_PENDING_KEY, appDevice, appSession } from "./app-storage";
import "../styles/app.css";

/** The Tafo app keeps its unconfirmed sales apart from a till in the same browser. */
choosePendingStore(APP_PENDING_KEY);

/** Where the app is: from a fresh phone to a signed-in waiter or courier (prototype renderPhone). */
type Stage =
  | { name: "starting" }
  | { name: "unreachable"; message: string }
  | { name: "pairing" }
  | { name: "blocked" }
  | { name: "terms" }
  | { name: "till" }
  /** The personal code: it also says whether this is the waiter app or the courier app (10/10/2026). */
  | { name: "signIn"; status: DeviceStatus }
  /** Right after Afmelden: it says so, and Aanmelden opens the sign-in (Furkan, 10/10/2026). */
  | { name: "signedOut"; status: DeviceStatus; who: string }
  | { name: "ready"; status: DeviceStatus; session: AppSession };

/** While the owner did not accept the terms yet, the app looks again now and then. */
const TERMS_RETRY_MS = 15_000;

const VIEWPORT = "width=device-width, initial-scale=1, viewport-fit=cover";

/**
 * "Zet op beginscherm" (besluiten 28/09: first a web app via a link, later the stores): the app's own
 * name, icon and colour when it is put on the home screen of an iPhone or an Android phone.
 */
function installable(): void {
  document.title = "Tafo";
  const add = (tag: "link" | "meta", attributes: Record<string, string>) => {
    const element = document.createElement(tag);
    for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
    document.head.append(element);
  };
  add("link", { rel: "manifest", href: "/app.webmanifest" });
  add("link", { rel: "apple-touch-icon", href: "/tafo-app-180.png" });
  add("meta", { name: "theme-color", content: "#c81319" });
  add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
  add("meta", { name: "apple-mobile-web-app-title", content: "Tafo" });
  // the red bar runs behind the clock and the battery of the phone; the bar keeps its words below them
  // (env(safe-area-inset-top) in app.css), and the list keeps its last line above the home bar
  document.querySelector('meta[name="viewport"]')?.setAttribute("content", VIEWPORT);
}

/**
 * Light or dark as the phone is set (Furkan, 08/10/2026: "nachtmode"): a courier at night is not blinded,
 * and the app changes along when the phone does at sunset. The till keeps its own Wit or Zwart.
 */
function followPhoneTheme(): () => void {
  const dark = window.matchMedia("(prefers-color-scheme: dark)");
  const apply = () => {
    document.documentElement.dataset.theme = dark.matches ? "dark" : "light";
  };
  dark.addEventListener("change", apply);
  return () => {
    dark.removeEventListener("change", apply);
  };
}

/**
 * De Tafo-app (ADR 0011) at /app: one app for waiters and couriers. It pairs with a code from the owner,
 * and the staff sign in with their own code, which says whether it is the waiter app or the courier app.
 */
/** The phone's light or dark on the page before anything is drawn: the opening starts in it (ADR 0037). */
function phoneThemeNow(): void {
  if (typeof window.matchMedia !== "function") return;
  document.documentElement.dataset.theme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function TafoApp() {
  // at the size of the app, also after the till on the owner's phone (see clearSize)
  useLayoutEffect(clearSize, []);
  useState(phoneThemeNow);
  // in the app from the stores the page itself never scrolls: put it back if iOS moved it (scroll.ts)
  useEffect(() => (inNativeApp() ? keepPageInPlace() : undefined), []);
  const [api] = useState(() =>
    createApiClient({ deviceToken: () => appDevice.read(), staffToken: () => appSession.read()?.token ?? null }),
  );
  // also without a token: the pairing this browser kept is asked first (check), so the pairing screen
  // never flashes by
  const [stage, setStage] = useState<Stage>({ name: "starting" });

  /** Asks the server where this device stands, then whether someone is still signed in. */
  const check = useCallback(async (): Promise<Stage> => {
    if (appDevice.read() === null) {
      // the phone emptied the storage, or the app was just put on the home screen (it starts empty there):
      // the pairing the browser kept comes back, without a new code (Furkan, 13/10/2026)
      const kept = await keptPairing(api, "app");
      if (kept.name === "unreachable") return kept;
      if (kept.name === "none") return { name: "pairing" };
      appDevice.save(kept.deviceToken);
    }
    let status: DeviceStatus;
    try {
      status = await api.request<DeviceStatus>("GET", "/device", { retry: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === "device_not_paired") {
        appDevice.clear();
        return { name: "pairing" };
      }
      return { name: "unreachable", message: error instanceof ApiError ? error.message : t.errors.unexpected };
    }
    if (status.device.role === "till") return { name: "till" };
    if (status.shop.status !== "active" && status.shop.status !== "trial") return { name: "blocked" };
    if (!status.terms.accepted) return { name: "terms" };
    const stored = appSession.read();
    // a new device: whoever signs in first makes it a waiter or a courier app
    if (status.device.role === "app") {
      appSession.clear();
      return { name: "signIn", status };
    }
    if (stored === null) return { name: "signIn", status };
    try {
      const current = await api.request<{ staff: StaffMember }>("GET", "/device/sessions/current", { retry: true });
      const session = { token: stored.token, staff: current.staff };
      appSession.save(session);
      return { name: "ready", status, session };
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        appSession.clear();
        return { name: "signIn", status };
      }
      return { name: "unreachable", message: error instanceof ApiError ? error.message : t.errors.unexpected };
    }
  }, [api]);

  const restart = useCallback(() => {
    void check().then(setStage);
  }, [check]);

  // an iPad, a tablet, or an iPhone Duo or other phone that unfolds: two columns as soon as the screen is wide
  const wide = useWideScreen();
  useEffect(installable, []);
  useEffect(followPhoneTheme, []);

  useEffect(restart, [restart]);

  // the owner accepts the terms elsewhere (mail, till): the app goes on by itself
  useEffect(() => {
    if (stage.name !== "terms" && stage.name !== "unreachable") return;
    const timer = setInterval(restart, TERMS_RETRY_MS);
    return () => {
      clearInterval(timer);
    };
  }, [stage.name, restart]);

  /** Errors that change what the app can do move it to the right screen. Returns true if handled. */
  const handleFatal = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof ApiError)) return false;
      if (error.code === "device_not_paired") {
        appDevice.clear();
        setStage({ name: "pairing" });
        return true;
      }
      // the 12 hours are over, or someone set the device up again: sign in (again)
      if (error.code === "staff_pin_required" || error.code === "device_role_not_chosen") {
        appSession.clear();
        restart();
        return true;
      }
      if (error.status === 423 || error.code === "shop_inactive") {
        restart();
        return true;
      }
      return false;
    },
    [restart],
  );

  let screen;
  switch (stage.name) {
    case "starting":
      screen = <AppWaiting kind="starting" />;
      break;
    case "unreachable":
      screen = <AppWaiting kind="unreachable" message={stage.message} onRetry={restart} />;
      break;
    case "blocked":
      screen = <AppWaiting kind="blocked" onRetry={restart} />;
      break;
    case "terms":
      screen = <AppWaiting kind="terms" onRetry={restart} />;
      break;
    case "till":
      screen = <AppWaiting kind="till" />;
      break;
    case "pairing":
      screen = (
        <AppPairing
          api={api}
          onPaired={(token) => {
            appDevice.save(token);
            appSession.clear();
            restart();
          }}
        />
      );
      break;
    case "signIn":
      screen = (
        <AppSignIn
          api={api}
          status={stage.status}
          onSignedIn={(session, role) => {
            appSession.save(session);
            setStage({
              name: "ready",
              status: { ...stage.status, device: { ...stage.status.device, role } },
              session,
            });
          }}
        />
      );
      break;
    case "signedOut":
      screen = (
        <AppSignedOut
          name={stage.who}
          onSignIn={() => {
            setStage({ name: "signIn", status: stage.status });
          }}
        />
      );
      break;
    case "ready":
      screen = (
        <AppShell
          // Ober ⇄ Koerier: the other app starts afresh, with its own data and live channel
          key={stage.status.device.role}
          api={api}
          status={stage.status}
          session={stage.session}
          handleFatal={handleFatal}
          onSignOut={() => {
            // "Afmelden": the session ends on the server too; offline it simply expires
            void api
              .request("DELETE", "/device/sessions/current", { staffToken: stage.session.token })
              .catch(() => undefined);
            appSession.clear();
            setStage({ name: "signedOut", status: stage.status, who: stage.session.staff.name });
          }}
          onModeSwitched={restart}
        />
      );
      break;
  }

  return (
    // at the bottom in the middle: a dot for what went well, words in red for what went wrong (08/10/2026)
    <ToastProvider max={5} visibleMs={2_500} place="phone">
      {/* the logo written at the opening, as on the till (Furkan, 10/10/2026: "er is geen welkomstanimatie in de app") */}
      <Opening>
        <WideScreen value={wide}>{screen}</WideScreen>
      </Opening>
    </ToastProvider>
  );
}
